import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ApiKeyService } from '../../services/ApiKeyService/api-key';
import { ApiKeyResponse } from '../../models/api-key.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { FilterSelect, FilterOption } from '../../shared/components/filter-select/filter-select';
import { PageSizeSelect } from '../../shared/components/page-size-select/page-size-select';
import { CreateApiKeyDialog } from '../../dialogs/create-api-key-dialog/create-api-key-dialog';
import { ConfirmDialog } from '../../dialogs/confirm-dialog/confirm-dialog';

// Mirrors the three values ApiKeys#statusOf derives from `active`/`revokedAt` — there's no literal
// `status` field on ApiKeyResponse, so this list exists only to drive the filter dropdown.
const STATUSES = ['ACTIVE', 'REVOKED', 'EXPIRED'];

function titleCase(status: string): string {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

@Component({
  selector: 'app-api-keys',
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, StatusBadge, ErrorBanner, EmptyState, FilterSelect, PageSizeSelect],
  templateUrl: './api-keys.html',
  styleUrl: './api-keys.scss',
})
export class ApiKeys implements OnInit {
  statusOptions: FilterOption[] = STATUSES.map((s) => ({ value: s, label: titleCase(s) }));
  statusFilter = '';

  apiKeys = signal<ApiKeyResponse[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');

  revokingId = signal<string | null>(null);

  readonly pageSizeOptions = [10, 20, 50, 100];
  pageSize = signal(10);
  page = signal(0);

  constructor(private apiKeyService: ApiKeyService, private dialog: MatDialog) {}

  // The backend's GET /companies/me/api-keys has no status query param and no status field at all
  // (statusOf derives it from active/revokedAt) — so filtering happens client-side over the full list.
  get filteredApiKeys(): ApiKeyResponse[] {
    const list = this.apiKeys();
    return this.statusFilter ? list.filter((k) => this.statusOf(k) === this.statusFilter) : list;
  }

  get statusFilterLabel(): string {
    return this.statusFilter ? titleCase(this.statusFilter) : 'All statuses';
  }

  get totalCount(): number {
    return this.filteredApiKeys.length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize()));
  }

  get pagedApiKeys(): ApiKeyResponse[] {
    const start = this.page() * this.pageSize();
    return this.filteredApiKeys.slice(start, start + this.pageSize());
  }

  get rangeStart(): number {
    return this.totalCount === 0 ? 0 : this.page() * this.pageSize() + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.page() + 1) * this.pageSize(), this.totalCount);
  }

  /** Page numbers to render, with -1 as an ellipsis placeholder, e.g. [0, -1, 4, 5, 6, -1, 19]. */
  get pageWindow(): number[] {
    const total = this.totalPages;
    const current = this.page();
    const pages: number[] = [];
    const add = (p: number) => {
      if (pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };

    add(0);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p > 0 && p < total - 1) {
        if (p > pages[pages.length - 1] + 1) {
          add(-1);
        }
        add(p);
      }
    }
    if (total - 1 > pages[pages.length - 1] + 1) {
      add(-1);
    }
    if (total > 1) {
      add(total - 1);
    }
    return pages;
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
  }

  onFilterChange(): void {
    this.page.set(0);
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.page()) {
      return;
    }
    this.page.set(page);
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.apiKeyService.list().subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.apiKeys.set(res.data);
        this.page.set(0);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load API keys.'));
      },
    });
  }

  statusOf(key: ApiKeyResponse): string {
    return key.active ? 'ACTIVE' : key.revokedAt ? 'REVOKED' : 'EXPIRED';
  }

  openCreateDialog(): void {
    this.dialog
      .open(CreateApiKeyDialog, { width: '480px' })
      .afterClosed()
      .subscribe((created) => {
        if (created) {
          this.load();
        }
      });
  }

  confirmRevokeDialog(key: ApiKeyResponse): void {
    this.dialog
      .open(ConfirmDialog, {
        width: '420px',
        data: {
          title: 'Revoke this API key?',
          message: `"${key.name}" will immediately stop working for any integration using it. This can't be undone.`,
          confirmLabel: 'Revoke',
          cancelLabel: 'Cancel',
          danger: true,
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.revoke(key);
        }
      });
  }

  private revoke(key: ApiKeyResponse): void {
    this.revokingId.set(key.id);

    this.apiKeyService.revoke(key.id).subscribe({
      next: () => {
        this.revokingId.set(null);
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.revokingId.set(null);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to revoke API key.'));
      },
    });
  }
}
